import { NavLink } from "react-router-dom";
import { useState } from "react";
import { useAuth, can } from "@/lib/auth";
import { useT, LANGS } from "@/lib/i18n";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import {
  LayoutDashboard,
  ArrowRightLeft,
  Landmark,
  Users,
  CalendarClock,
  FileBarChart,
  Boxes,
  UploadCloud,
  Menu,
  X,
  Wallet,
  LogOut,
  Sparkles,
  Calculator,
  Building2,
  Truck,
  ClipboardList,
  NotebookPen,
  PackageSearch,
  KeyRound,
  ShieldCheck,
  ScrollText,
  Globe,
} from "lucide-react";

const links = [
  { to: "/", tkey: "nav.dashboard", icon: LayoutDashboard, testid: "nav-dashboard", perm: "dashboard" },
  { to: "/hareketler", tkey: "nav.transactions", icon: ArrowRightLeft, testid: "nav-transactions", perm: "transactions" },
  { to: "/hesaplar", tkey: "nav.accounts", icon: Landmark, testid: "nav-accounts", perm: "accounts" },
  { to: "/cari", tkey: "nav.contacts", icon: Users, testid: "nav-contacts", perm: "contacts" },
  { to: "/musteriler", tkey: "nav.customers", icon: Building2, testid: "nav-customers", perm: "customers" },
  { to: "/tedarikciler", tkey: "nav.suppliers", icon: Truck, testid: "nav-suppliers", perm: "suppliers" },
  { to: "/notlar", tkey: "nav.notes", icon: NotebookPen, testid: "nav-notes", perm: "notes" },
  { to: "/stok", tkey: "nav.stock", icon: Boxes, testid: "nav-stock", perm: "stock" },
  { to: "/urunler", tkey: "nav.products", icon: PackageSearch, testid: "nav-products", perm: "products" },
  { to: "/siparis", tkey: "nav.orders", icon: ClipboardList, testid: "nav-orders", perm: "orders" },
  { to: "/maliyet", tkey: "nav.cost", icon: Calculator, testid: "nav-cost", perm: "cost" },
  { to: "/odemeler", tkey: "nav.payments", icon: CalendarClock, testid: "nav-payments", perm: "payments" },
  { to: "/raporlar", tkey: "nav.reports", icon: FileBarChart, testid: "nav-reports", perm: "reports" },
  { to: "/asistan", tkey: "nav.assistant", icon: Sparkles, testid: "nav-assistant", perm: "assistant" },
  { to: "/veri-guncelle", tkey: "nav.import", icon: UploadCloud, testid: "nav-import", perm: "import" },
  { to: "/personel", tkey: "nav.personnel", icon: ShieldCheck, testid: "nav-personnel", adminOnly: true },
  { to: "/islem-logu", tkey: "nav.audit", icon: ScrollText, testid: "nav-audit", adminOnly: true },
];

export default function Sidebar() {
  const [open, setOpen] = useState(false);
  const [cpOpen, setCpOpen] = useState(false);
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useT();

  const nav = (
    <nav className="flex flex-col gap-1 px-3">
      {links.filter((l) => (l.adminOnly ? user?.role === "admin" : can(user, l.perm))).map((l) => {
        const Icon = l.icon;
        return (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === "/"}
            data-testid={l.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? "bg-primary/15 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              }`
            }
          >
            <Icon className="h-[18px] w-[18px] shrink-0" />
            <span>{t(l.tkey)}</span>
          </NavLink>
        );
      })}
    </nav>
  );

  return (
    <>
      {/* Mobile top bar */}
      <div className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between border-b border-border bg-card px-4 py-3 lg:hidden">
        <div className="flex items-center gap-2">
          <img src="/arslan-logo.png" alt="Arslan" className="h-8 w-8 rounded-lg object-contain" />
          <span className="font-display text-lg font-bold">Arslan</span>
        </div>
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="text-foreground">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-30 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />
      )}

      <aside
        className={`fixed top-0 left-0 z-30 flex h-screen w-64 flex-col border-r border-border bg-card transition-transform duration-300 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        } pt-16 lg:pt-0`}
      >
        <div className="hidden items-center gap-2.5 px-6 py-6 lg:flex">
          <img src="/arslan-logo.png" alt="Arslan" className="h-10 w-10 rounded-lg object-contain" />
          <div>
            <div className="font-display text-lg font-bold leading-none">Arslan</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-1">
              {t("app.subtitle")}
            </div>
          </div>
        </div>
        <div className="mt-2 lg:mt-0 flex-1 overflow-y-auto">{nav}</div>
        <div className="border-t border-border px-3 py-3">
          <div className="mb-1 flex items-center gap-1.5 px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Globe className="h-3.5 w-3.5" /> {t("common.language")}
          </div>
          <div className="flex gap-1.5">
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-${l.code}`}
                onClick={() => setLang(l.code)}
                className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors ${lang === l.code ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}
              >
                {l.short}
              </button>
            ))}
          </div>
        </div>
        <div className="border-t border-border p-4">
          <div className="flex items-center gap-3">
            <img
              src="https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NDM3NzU3N3ww&ixlib=rb-4.1.0&q=85"
              alt="Kullanıcı"
              className="h-9 w-9 rounded-full object-cover"
            />
            <button
              data-testid="account-settings-button"
              onClick={() => setCpOpen(true)}
              title="Şifre değiştir"
              className="min-w-0 flex-1 text-left"
            >
              <div className="truncate text-sm font-medium hover:text-primary">{user?.name || "İşletme Sahibi"}</div>
              <div className="truncate text-xs text-muted-foreground">{user?.email || ""}</div>
            </button>
            <button
              data-testid="change-password-button"
              onClick={() => setCpOpen(true)}
              title="Şifre Değiştir"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-primary/15 hover:text-primary"
            >
              <KeyRound className="h-[18px] w-[18px]" />
            </button>
            <button
              data-testid="logout-button"
              onClick={logout}
              title="Çıkış Yap"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-rose-500/15 hover:text-rose-400"
            >
              <LogOut className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>
      </aside>
      <ChangePasswordDialog open={cpOpen} onOpenChange={setCpOpen} />
    </>
  );
}
